// Production single-site sprites. The site coordinate is the canvas centre / pin tip.
// Colours deliberately share the bubble palette, so the later colour round has one source.
import {bucket, bubbleStyle, PIXEL_RATIO, markerFont} from './bubbles.mjs';
import {powerRange, isDcPower, atDisplayedPeak} from './power.mjs';
import {dimColor, colorContrast} from './palette.mjs';
import {drawHitLabel} from './hits.mjs';
import {FOCUS_DIM} from './dot-variants.mjs';
export const PIN_WIDTH = 38, PIN_HEIGHT = 50;
export const pinLabel = c => c.total && c.out === c.total ? '✕' : !c.known ? `?/${c.total}` : `${c.available}/${c.total}`;
export const contrast=colorContrast;
export function pinStyle(b,theme,profile={}) {
  const fill=bubbleStyle(b,theme,profile).fill;
  const inner=dimColor(fill,44);
  return {fill,inner,text:contrast('#ffffff',inner)>=4.5?'#ffffff':'#10161f',border:'#0e131a'};
}
// Catalog powers/prices describe the entire site, independent of the active point filter.
const secondaryCache = new WeakMap();
export function siteSecondary(site = {}) {
  const previous=secondaryCache.get(site);
  if(previous && previous.version===site.secondaryVersion)return previous.value;
  let kwMin=Infinity,kwMax=0,ct=0;
  for(const p of site.points||[]) {
    if(p.kw>0){kwMin=Math.min(kwMin,p.kw);kwMax=Math.max(kwMax,p.kw);}
    if(p.ct>0)ct=ct?Math.min(ct,p.ct):p.ct;
  }
  if(!kwMax){kwMax=Math.max(site.ac||0,site.dc||0);kwMin=kwMax;}
  const kwDc=site.points?.length
    ? site.points.some(p=>atDisplayedPeak(p.kw,kwMax)&&isDcPower(p.current,p.kw))
    : atDisplayedPeak(site.dc||0,kwMax);
  const value={kwMin:kwMax?kwMin:0,kwMax,ct,kwDc};
  secondaryCache.set(site,{version:site.secondaryVersion,value});
  return value;
}
export function secondaryLabel(p,mode) {
  if(bucket(p)==='out')return '';
  if(mode==='price')return p.ct>0?(p.ct/100).toFixed(2).replace('.',',')+' €':'';
  if(mode==='kw'&&p.kwMax>0)return powerRange(p.kwMin,p.kwMax)+' kW';
  return '';
}
// Badge text plus tone: only the power badge of a DC site is tinted, like .plug-power.is-dc in the details.
export const secondaryBadge = (p,mode) => {const text=secondaryLabel(p,mode);return {text,dc:!!(text&&mode==='kw'&&p.kwDc)};};
const asBadge = b => typeof b==='string'?{text:b,dc:false}:{text:b?.text||'',dc:!!b?.dc};
// Canvas copies of the --pin-badge-* and --accent / --accent-soft tokens (style.css); the soft tint is composed onto the badge base.
export const BADGE_TONES = Object.freeze({
  light: Object.freeze({fill:'#ffffff',stroke:'#8794a3',text:'#17212f',accent:'#0756b4',soft:0x1a/255}),
  dark: Object.freeze({fill:'#151c26',stroke:'#4a5568',text:'#e6ebf2',accent:'#5ea2ff',soft:0x24/255})});
const mix = (a,b,t) => '#'+[1,3,5].map(i=>Math.round(parseInt(a.slice(i,i+2),16)*(1-t)+parseInt(b.slice(i,i+2),16)*t).toString(16).padStart(2,'0')).join('');
export function badgeColors(theme,dc=false) {
  const c=BADGE_TONES[theme==='dark'?'dark':'light'];
  return dc?{fill:mix(c.fill,c.accent,c.soft),stroke:mix(c.fill,c.accent,.55),text:c.accent}:{fill:c.fill,stroke:c.stroke,text:c.text};
}
export const PIN_SIZE = 90;
export const pinScale = () => PIN_SIZE/100;
export const pinNumberSize = label => label.length<=3?14:label.length===4?12.5:11;
export const pinFont = markerFont;
export const pinPixelRatio = (scale=1) => Math.min(PIXEL_RATIO,Math.max(1,Math.ceil((globalThis.devicePixelRatio||PIXEL_RATIO)*scale)));
export const pinSpriteID = (p,theme,mode,profile={},pixelRatio=pinPixelRatio()) => {const b=secondaryBadge(p,mode);return `pin|${theme}|${bucket(p)}|${pinLabel(p)}|${b.text}|${b.dc?'dc':''}|${pixelRatio}`;};
const badgeWidths = new Map();
export function badgeWidth(label,ctx=null,profile={}) {
  const key=pinFont(profile)+'|'+label;
  if(!badgeWidths.has(key)){ctx??=document.createElement('canvas').getContext('2d');ctx.font=`600 11px ${pinFont(profile)}`;badgeWidths.set(key,Math.min(94,Math.ceil(ctx.measureText(label).width)+12));}
  return badgeWidths.get(key);
}
// Badge below the pin tip, centred on x; shared by map sprites and the legend sample.
export function drawBadge(ctx,badge,theme,profile={},x=48,top=80) {
  const {text,dc}=asBadge(badge),c=badgeColors(theme,dc);
  ctx.font=`600 11px ${pinFont(profile)}`;ctx.textAlign='center';ctx.textBaseline='middle';
  const w=badgeWidth(text,ctx,profile);
  ctx.beginPath();ctx.roundRect(x-w/2,top,w,19,6);ctx.fillStyle=c.fill;ctx.fill();
  ctx.strokeStyle=c.stroke;ctx.lineWidth=1;ctx.stroke();ctx.fillStyle=c.text;
  ctx.fillText(text,x,top+10,w-8);
}
export function drawPin(label,b,theme,canvas=document.createElement('canvas'),profile={},badge='',rank=0,scale=1,pixelRatio=PIXEL_RATIO) {
  const logicalWidth=Math.ceil(96*Math.max(1,pinScale(profile)));
  canvas.width=Math.ceil(logicalWidth*scale*pixelRatio);canvas.height=Math.ceil(152*scale*pixelRatio);
  if(canvas.style){canvas.style.width=`${logicalWidth*scale}px`;canvas.style.height=`${152*scale}px`;}
  const ctx=canvas.getContext('2d',{willReadFrequently:true});ctx.scale(pixelRatio*scale,pixelRatio*scale);ctx.translate((logicalWidth-96)/2,0);
  const c=pinStyle(b,theme,profile);
  // Head centre (48,45), exact geographic tip (48,76), 38 × 50 maximum.
  const size=pinScale(profile);
  ctx.save();ctx.translate(48,76);ctx.scale(size,size);ctx.translate(-48,-76);
  const tangent=Math.asin(18.1/31);
  ctx.beginPath();ctx.moveTo(48,76);ctx.arc(48,45,18.1,Math.PI-tangent,2*Math.PI+tangent);ctx.closePath();
  ctx.fillStyle=c.fill;ctx.fill();ctx.lineWidth=1;ctx.strokeStyle=c.border;ctx.stroke();
  if(b==='out') {
    ctx.save();ctx.clip();ctx.strokeStyle='#ffffff66';ctx.lineWidth=2;
    for(let x=0;x<110;x+=6){ctx.beginPath();ctx.moveTo(x,20);ctx.lineTo(x-60,80);ctx.stroke();}ctx.restore();
  }
  ctx.beginPath();ctx.arc(48,45,14.8,0,2*Math.PI);ctx.fillStyle=c.inner;ctx.fill();
  ctx.fillStyle=c.text;ctx.font=`600 ${pinNumberSize(label,profile)}px ${pinFont(profile)}`;
  const width=ctx.measureText(label).width;if(width>28)ctx.font=`600 ${pinNumberSize(label,profile)*28/width}px ${pinFont(profile)}`;
  ctx.textAlign='center';ctx.textBaseline='middle';ctx.fillText(label,48,45.5);
  if(asBadge(badge).text)drawBadge(ctx,badge,theme,profile);
  ctx.restore();
  if(rank)ctx.drawImage(drawHitLabel(rank,theme,undefined,pixelRatio*scale,profile),3,29,32,32);
  return canvas;
}
export function pinSpriteGeometry(scale,badgeWidth=0) {
  const top=76-51*scale,height=Math.ceil((badgeWidth?76+25*scale:76+scale)-top);
  return {top,height,width:Math.ceil(Math.max(42*scale,badgeWidth?badgeWidth+2*scale:0)),offset:top+height/2-76};
}
function spriteCanvas(id,profile) {
  const [,theme,b,label,badge,tone,ratio]=id.split('|'),pixelRatio=Number(ratio)||PIXEL_RATIO;
  const scale=pinScale(profile),full=drawPin(label,b,theme,undefined,profile,{text:badge,dc:tone==='dc'},0,1,pixelRatio),canvas=document.createElement('canvas');
  const {width,height,top}=pinSpriteGeometry(scale,badge?badgeWidth(badge,null,profile)*scale:0);
  canvas.width=width*pixelRatio;canvas.height=height*pixelRatio;
  canvas.getContext('2d').drawImage(full,(full.width/pixelRatio/2-width/2)*pixelRatio,top*pixelRatio,canvas.width,canvas.height,0,0,canvas.width,canvas.height);
  return canvas;
}
function image(map,id,profile,update=false){
  if(!update&&map.hasImage(id))return;
  const canvas=spriteCanvas(id,profile),pixels=canvas.getContext('2d').getImageData(0,0,canvas.width,canvas.height);
  if(update)map.updateImage(id,pixels);else map.addImage(id,pixels,{pixelRatio:Number(id.split('|')[6])||PIXEL_RATIO});
}
const owned = id => id.startsWith('pin|');
export function installPinSprites(map,profile=()=>({})) {map.on('styleimagemissing',e=>{if(owned(e.id))image(map,e.id,profile());});}
export function preparePinProperties(features,theme,mode,profile={},lookup=null,resolution=pinPixelRatio()) {
  for(const f of features){
    const p=f.properties;if(lookup)Object.assign(p,siteSecondary(lookup(p.id)));
    const badge=p.hideBadge?'':secondaryLabel(p,mode);p.pinBadge=badge;p.pinBadgeWidth=badge?badgeWidth(badge,null,profile)*pinScale(profile):0;
    p.pinScale=pinScale(profile);p.pinOffset=pinSpriteGeometry(p.pinScale,p.pinBadgeWidth).offset;
    p.pinOffsetVector=[0,p.pinOffset];
    p.pinSprite=pinSpriteID(p,theme,p.hideBadge?'off':mode,profile,p.rank?pinPixelRatio():resolution);
  }
}
export function registerPinSprites(map,features,profile={}) {for(const f of features)image(map,f.properties.pinSprite,profile);}
export function preparePinSprites(map,features,theme,mode,profile={},lookup=null,resolution=pinPixelRatio()) {
  preparePinProperties(features,theme,mode,profile,lookup,resolution);
  registerPinSprites(map,features,profile);
}
export function refreshPinSprites(map,profile){for(const id of map.listImages())if(owned(id))image(map,id,profile,true);}
export const focusOpacity = active => active?['case',['has','rank'],1,FOCUS_DIM]:1;
